import axios, { AxiosResponse } from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { serializeAxiosError } from '../../reducers/reducer.utils';
import { AppThunk } from '../../../store';


const AUTH_TOKEN_KEY = 'jhi-authenticationToken';

export const initialState = {
    loading: false,
    isAuthenticated: false,
    loginSuccess: false,
    loginError: false,
    showModalLogin: false,
    account: {} as any,
    errorMessage: null as string | null,
    redirectMessage: null as string | null,
    sessionHasBeenFetched: false,
    logoutUrl: null as string | null,
};
export type AuthenticationState = Readonly<typeof initialState>;

// Actions

export const getSession = (): AppThunk => (dispatch, getState) => {
    dispatch(getAccount());
};

export const getAccount = createAsyncThunk('authentication/get_account', async () => axios.get<any>('api/account'), {
    serializeError: serializeAxiosError,
});

interface IAuthParams {
    username: string;
    password: string;
    rememberMe?: boolean;
}

export const authenticate = createAsyncThunk(
    'authentication/login',
    async (auth: IAuthParams) => axios.post<any>('api/authenticate', auth),
    {
        serializeError: serializeAxiosError,
    },
);

export const login: (username: string, password: string, rememberMe?: boolean) => AppThunk =
    (username, password, rememberMe = false) =>
        async dispatch => {
            const result = await dispatch(authenticate({ username, password, rememberMe }));
            const response = result.payload as AxiosResponse;
            const bearerToken = response?.headers?.authorization;
            if (bearerToken && bearerToken.slice(0, 7) === 'Bearer ') {
                const jwt = bearerToken.slice(7, bearerToken.length);

            }
            dispatch(getSession());
        };

export const clearAuthToken = () => {

};

export const logout: () => AppThunk = () => dispatch => {
    clearAuthToken();
    dispatch(logoutSession());
};

export const clearAuthentication = (messageKey: any) => (dispatch: any) => {
    clearAuthToken();
    dispatch(authError(messageKey));
    dispatch(clearAuth());
};

export const AuthenticationSlice = createSlice({
    name: 'authentication',
    initialState: initialState as AuthenticationState,
    reducers: {
        logoutSession() {
            return {
                ...initialState,
                showModalLogin: true,
            };
        },
        authError(state, action) {
            return {
                ...state,
                showModalLogin: true,
                redirectMessage: action.payload,
            };
        },
        clearAuth(state) {
            return {
                ...state,
                loading: false,
                showModalLogin: true,
                isAuthenticated: false,
            };
        },
    },
    extraReducers(builder) {
        builder
            .addCase(authenticate.rejected, (state, action) => ({
                ...initialState,
                errorMessage: action.error.message ?? null,
                showModalLogin: true,
                loginError: true,
            }))
            .addCase(authenticate.fulfilled, state => ({
                ...state,
                loading: false,
                loginError: false,
                showModalLogin: false,
                loginSuccess: true,
            }))
            .addCase(getAccount.rejected, (state, action) => ({
                ...state,
                loading: false,
                isAuthenticated: false,
                sessionHasBeenFetched: true,
                showModalLogin: true,
                errorMessage: action.error.message ?? null,
            }))
            .addCase(getAccount.fulfilled, (state, action) => {
                const isAuthenticated = action.payload && action.payload.data && action.payload.data.activated;
                return {
                    ...state,
                    isAuthenticated,
                    loading: false,
                    sessionHasBeenFetched: true,
                    account: action.payload.data,
                };
            })
            .addCase(authenticate.pending, state => {
                state.loading = true;
            })
            .addCase(getAccount.pending, state => {
                state.loading = true;
            });
    },
});

export const { logoutSession, authError, clearAuth } = AuthenticationSlice.actions;

// Reducer
export default AuthenticationSlice.reducer;
